import { useObservable } from "dexie-react-hooks"
import { useEffect } from "react"
import { Capacitor } from "@capacitor/core"
import { db } from "@/db/schema"

/**
 * useTokenSync — syncs Dexie Cloud tokens to Capacitor Preferences.
 *
 * Ported from v1's `src/hooks/useTokenSync.ts`.
 *
 * On iOS, IndexedDB is unavailable during background execution (push
 * notifications, background fetch). This hook copies the access token,
 * refresh token, and expiration timestamp to Capacitor Preferences
 * (native key-value storage) whenever they change, so background tasks
 * can authenticate API calls without IndexedDB.
 *
 * Keys stored:
 *   - dexie_user_token        — access token (~1h lifetime)
 *   - dexie_refresh_token     — refresh token (~315 years)
 *   - dexie_token_expiration  — expiration timestamp
 *
 * Only runs on native platforms.
 */
export function useTokenSync(): void {
  const currentUser = useObservable(db.cloud.currentUser)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const syncTokens = async () => {
      try {
        // Dynamic import — @capacitor/preferences may not be bundled on web
        const { Preferences } = await import("@capacitor/preferences")

        const accessToken = currentUser?.accessToken as string | undefined
        const refreshToken = currentUser?.refreshToken as string | undefined
        const accessTokenExpiration = currentUser?.accessTokenExpiration

        if (accessToken) {
          await Preferences.set({ key: "dexie_user_token", value: accessToken })

          if (accessTokenExpiration) {
            await Preferences.set({
              key: "dexie_token_expiration",
              value: String(accessTokenExpiration),
            })
          }

          if (refreshToken) {
            await Preferences.set({
              key: "dexie_refresh_token",
              value: refreshToken,
            })
          }
        } else {
          // No token — user logged out, clear stored tokens
          await Preferences.remove({ key: "dexie_user_token" })
          await Preferences.remove({ key: "dexie_refresh_token" })
          await Preferences.remove({ key: "dexie_token_expiration" })
        }
      } catch (error) {
        console.warn("[TokenSync] Failed to sync tokens to Preferences:", error)
      }
    }

    void syncTokens()
  }, [
    currentUser?.accessToken,
    currentUser?.refreshToken,
    currentUser?.accessTokenExpiration,
  ])
}
